import React from "react";

export const getColourPresentation = (colour) => {
  const normalizedColour = String(colour || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  if (!normalizedColour) return null;

  const finishMap = [
    {
      match: ["metallic bronze", "bronze metallic"],
      swatchStyle: {
        background: "linear-gradient(135deg, #6f4e37 0%, #b87333 100%)",
        borderColor: "#8b5e3c",
      },
      textStyle: { color: "#8b5e3c" },
    },
    {
      match: ["black silver", "silver black"],
      swatchStyle: {
        background: "linear-gradient(135deg, #111827 0%, #9ca3af 100%)",
        borderColor: "#6b7280",
      },
      textStyle: { color: "#4b5563" },
    },
    {
      match: ["stainless steel"],
      swatchStyle: {
        background: "linear-gradient(135deg, #d1d5db 0%, #9ca3af 50%, #e5e7eb 100%)",
        borderColor: "#9ca3af",
      },
      textStyle: { color: "#6b7280" },
    },
    {
      match: ["matt black", "matte black"],
      swatchStyle: {
        backgroundColor: "#1f2937",
        borderColor: "#1f2937",
      },
      textStyle: { color: "#1f2937" },
    },
    {
      match: ["flat white"],
      swatchStyle: {
        backgroundColor: "#f9fafb",
        borderColor: "#d1d5db",
      },
      textStyle: { color: "#6b7280" },
    },
    {
      match: ["dark timber"],
      swatchStyle: {
        backgroundColor: "#6b4423",
        borderColor: "#6b4423",
      },
      textStyle: { color: "#6b4423" },
    },
    {
      match: ["light timber"],
      swatchStyle: {
        backgroundColor: "#c19a6b",
        borderColor: "#c19a6b",
      },
      textStyle: { color: "#a16207" },
    },
    {
      match: ["gun metal", "gunmetal"],
      swatchStyle: {
        backgroundColor: "#4b5563",
        borderColor: "#4b5563",
      },
      textStyle: { color: "#4b5563" },
    },
    {
      match: ["rose gold"],
      swatchStyle: {
        background: "linear-gradient(135deg, #b76e79 0%, #e0bfb8 100%)",
        borderColor: "#b76e79",
      },
      textStyle: { color: "#b76e79" },
    },
    {
      match: ["sand"],
      swatchStyle: {
        backgroundColor: "#d6c6a5",
        borderColor: "#d6c6a5",
      },
      textStyle: { color: "#a16207" },
      badgeStyle: {
        background: "#f5efe4",
        borderColor: "#d6c6a5",
        color: "#8a6a2f",
      },
    },
    {
      match: ["cream magnolia", "magnolia cream"],
      swatchStyle: {
        backgroundColor: "#f3e7cf",
        borderColor: "#e7d7b7",
      },
      textStyle: { color: "#8b7355" },
      badgeStyle: {
        background: "#fcf7ee",
        borderColor: "#e7d7b7",
        color: "#8b7355",
      },
    },
    {
      match: ["light pink", "soft pink", "baby pink"],
      swatchStyle: {
        backgroundColor: "#f7b6cb",
        borderColor: "#e88cb0",
      },
      textStyle: { color: "#a43e6b" },
      badgeStyle: {
        background: "#fff0f5",
        borderColor: "#e9a8c4",
        color: "#a43e6b",
      },
    },
  ];

  const matchedFinish = finishMap.find(({ match }) =>
    match.some((candidate) => normalizedColour.includes(candidate)),
  );

  if (matchedFinish) {
    return {
      ...matchedFinish,
      badgeStyle:
        matchedFinish.badgeStyle || {
          background: "#f8fafc",
          borderColor:
            matchedFinish.swatchStyle?.borderColor || "#d1d5db",
          color: matchedFinish.textStyle?.color || "#4b5563",
        },
    };
  }

  const tokenMap = {
    black: "#111827",
    white: "#ffffff",
    flat: "#ffffff",
    matt: "#111827",
    matte: "#111827",
    blue: "#2563eb",
    red: "#dc2626",
    green: "#16a34a",
    yellow: "#eab308",
    orange: "#f97316",
    purple: "#9333ea",
    pink: "#ec4899",
    brown: "#8b5e3c",
    bronze: "#b87333",
    cream: "#f3e7cf",
    magnolia: "#efe2c4",
    beige: "#e8d7b3",
    ivory: "#f8f4e8",
    light: "#d1d5db",
    grey: "#6b7280",
    gray: "#6b7280",
    silver: "#9ca3af",
    gold: "#d4a017",
    rose: "#b76e79",
    steel: "#94a3b8",
    stainless: "#cbd5e1",
    timber: "#8b5e3c",
    wood: "#8b5e3c",
    dark: "#374151",
    gun: "#4b5563",
    metal: "#6b7280",
    metallic: "#6b7280",
    sand: "#d6c6a5",
  };

  const tokens = normalizedColour.split(" ").filter(Boolean);
  const matchedColours = tokens
    .map((token) => tokenMap[token])
    .filter(Boolean);

  if (matchedColours.length === 0) {
    return {
      textStyle: { color: "#6b7280" },
      badgeStyle: {
        background: "#f8fafc",
        borderColor: "#d1d5db",
        color: "#6b7280",
      },
    };
  }

  const backgroundColor =
    matchedColours.length === 1
      ? matchedColours[0]
      : `linear-gradient(135deg, ${matchedColours.join(", ")})`;
  const primaryColour = matchedColours[0];
  const needsBorder =
    primaryColour === "#ffffff" ||
    primaryColour === "#f3e7cf" ||
    primaryColour === "#efe2c4" ||
    primaryColour === "#e8d7b3" ||
    primaryColour === "#d6c6a5" ||
    primaryColour === "#eab308";
  const textColor =
    primaryColour === "#ffffff"
      ? "#4b5563"
      : needsBorder
        ? "#8a6a2f"
        : primaryColour;

  return {
    swatchStyle: {
      backgroundColor,
      background: backgroundColor,
      borderColor: needsBorder ? "#d1d5db" : primaryColour,
    },
    textStyle: {
      color: textColor,
    },
    badgeStyle: {
      background: needsBorder ? "#fcfaf5" : "#f8fafc",
      borderColor: needsBorder ? "#e7d7b7" : `${primaryColour}33`,
      color: textColor,
    },
  };
};

const ColourMeta = ({ label, colour }) => {
  if (!colour) return null;

  const colourPresentation = getColourPresentation(colour);

  return (
    <span className="sale-colour-meta">
      {label ? <span className="sale-colour-meta-label">{label}</span> : null}
      <span
        className="sale-colour-pill"
        style={colourPresentation?.badgeStyle}
      >
        {colourPresentation?.swatchStyle ? (
          <span
            className="sale-colour-swatch"
            style={colourPresentation.swatchStyle}
            aria-hidden="true"
          />
        ) : null}
        <span style={colourPresentation?.textStyle}>{colour}</span>
      </span>
    </span>
  );
};

export default ColourMeta;
